import { useEffect, useMemo, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFloppyDisk } from "@fortawesome/free-regular-svg-icons";
import { faPlus } from "@fortawesome/free-solid-svg-icons";
import { faTrashCan } from "@fortawesome/free-solid-svg-icons/faTrashCan";
import { faEraser } from "@fortawesome/free-solid-svg-icons/faEraser";

import ApiConnector from "../connector/ApiConnector";
import UtilService from "../service/UtilService";
import GetFileViaS3 from "../component/S3FileAnchor";
import MerchantHotspotStatusOption from "../component/configPage/MerchantHotspotStatusOption";
import { useExport } from "../context/ExportContext";

import { useTableModel, makeKey } from "../service/TableModelUtilService";

function normalizeHotspots(rows) {
  const toIsoOrNull = (x) => {
    if (!x) return null;
    const d = new Date(x);
    return Number.isNaN(d.getTime()) ? null : d.toISOString();
  };
  return rows
    .map((r) => ({
      id: String(r.id ?? ""),
      status: String(r.status ?? ""),
      isCreatedByAutoUpdate: !!r.isCreatedByAutoUpdate,
      expirationTime: r.isCreatedByAutoUpdate ? null : toIsoOrNull(r.expirationTime),
    }))
    .sort((a, b) => {
      if (a.isCreatedByAutoUpdate !== b.isCreatedByAutoUpdate) {
        return a.isCreatedByAutoUpdate ? 1 : -1;
      }
      const s = a.status.localeCompare(b.status);
      if (s !== 0) return s;
      return a.id.localeCompare(b.id);
    });
}

const NEW_ENTRY = {
  id: "",
  status: "SEMI_HOTSPOT",
  isCreatedByAutoUpdate: false,
  expirationTime: null,
};

export default function Component() {
  const columns = useMemo(() => [], []);

  const {
    rows,
    setRows,
    addRow,
    updateRow,
    removeRow,
    setBaselineFromRows,
    canSave,
    keyField,
  } = useTableModel({
    initialRows: [],
    columns,
    keyField: "_key",
    initialSortBy: null,
    initialSortDir: "asc",
    normalizeForSignature: normalizeHotspots,
  });

  const { isExportActive } = useExport();

  const [csvUrl, setCsvUrl] = useState("");
  const [merchantInfos, setMerchantInfos] = useState(null);

  const nameById = useMemo(() => {
    if (!merchantInfos) return new Map();
    return new Map(merchantInfos.map((mi) => [String(mi.id), mi.name]));
  }, [merchantInfos]);

  useEffect(() => {
    let revoked = false;
    let previousUrl = null;

    async function load() {
      const raw = await ApiConnector.getMerchantHotspots();

      const sorted = [...raw]
        .sort((a, b) => String(a.id ?? "").localeCompare(String(b.id ?? "")))
        .sort((a, b) => String(a.status ?? "").localeCompare(String(b.status ?? "")))
        .sort((a, b) =>
          (a.isCreatedByAutoUpdate ? 1 : 0) - (b.isCreatedByAutoUpdate ? 1 : 0)
        );

      const withKeys = sorted.map((r) => (r._key ? r : { ...r, _key: makeKey() }));
      setRows(withKeys);
      setBaselineFromRows(withKeys);

      const codes = sorted.map((h) => h.id);
      const infos = await ApiConnector.getMerchantInfos(codes);
      setMerchantInfos(infos);

      const csvRows = sorted.map((h) => ({
        ...h,
        merchantName: infos.find((mi) => mi.id === h.id)?.name || "",
      }));
      const url = UtilService.convertJsonArrayToDownloadableCsvFileUrl(csvRows);
      previousUrl = url;
      setCsvUrl(url);
    }

    load();

    return () => {
      if (previousUrl && !revoked) {
        try {
          URL.revokeObjectURL(previousUrl);
        } catch {}
        revoked = true;
      }
    };
  }, []);

  const onChangeId = (row, next) => updateRow(row[keyField], { id: next });
  const onChangeStatus = (row, next) => updateRow(row[keyField], { status: next });
  const onChangeHours = (row, nextHours) => {
    const h = String(nextHours).trim();
    const expirationTime =
      h === "" ? null : UtilService.mapHoursRemainingToIsoOffsetDateTimeString(Number(h));
    updateRow(row[keyField], { expirationTime });
  };
  const onClearExpiration = (row) => updateRow(row[keyField], { expirationTime: null });
  const onAdd = () => addRow(NEW_ENTRY);
  const onRemove = (row) => removeRow(row[keyField]);

  const save = async () => {
    if (!canSave) return;

    const payload = rows.map(({ _key, ...rest }) => rest);
    const dupes = payload
      .map((h) => String(h.id ?? ""))
      .filter((id, i, a) => id && a.indexOf(id) !== i);
    if (dupes.length) {
      window.alert(
        "New hotspots can't be saved because the list contains duplicities: " +
        Array.from(new Set(dupes)).join(", ")
      );
      return;
    }

    const result = window.confirm(
      "New hotspots will be set. This operation can't be undone."
    );
    if (!result) return;
    await ApiConnector.replaceMerchantHotspots(payload);
    setBaselineFromRows(rows);
  };

  return (
    <>
      <header className="subheader header-with-save">
        <h1 className="header-title">Config - Hotspot rule</h1>
        <span className="header-save">
          <FontAwesomeIcon
            icon={faFloppyDisk}
            size="2xl"
            className="icon-clickable"
            onClick={canSave ? save : undefined}
            title={canSave ? "Save changes" : "No changes to save"}
            style={{
              opacity: canSave ? 1 : 0.4,
              cursor: canSave ? "pointer" : "not-allowed",
              pointerEvents: canSave ? "auto" : "none",
            }}
            aria-disabled={!canSave}
          ></FontAwesomeIcon>
        </span>
      </header>

      <main>
        <div
          className="row"
          style={{ alignItems: "center", justifyContent: "space-between", marginBottom: 0 }}
        >
          <div className="col">
            <h2 style={{ marginBottom: 6 }}>Merchant hotspots</h2>
            <a href={csvUrl} download="hotspots.csv">
              Export Merchant Hotspots
            </a>
            <br />
            <GetFileViaS3
              caption="Export Merchant Infos"
              disabled={isExportActive}
              onFirstClick={() => ApiConnector.generateMerchantInfoExcelToS3()}
              onForceClick={() =>
                ApiConnector.generateMerchantInfoExcelToS3({ forceRegenerate: true })
              }
              onLoadFiles={() => ApiConnector.listExportFiles()}
            ></GetFileViaS3>
          </div>
          <div className="col" />
        </div>

        <table className="config-table-double table-with-button">
          <thead>
          <tr>
            <th>Merchant code</th>
            <th>Status</th>
            <th>
              Remaining <small>[hours]</small>
            </th>
            <th>
              Created by
              <br />
              auto update
            </th>
            <th>Merchant name</th>
            <th style={{ width: 44, textAlign: "right" }}>
              <FontAwesomeIcon
                icon={faPlus}
                className="icon-clickable"
                onClick={onAdd}
              ></FontAwesomeIcon>
            </th>
          </tr>
          </thead>

          <tbody>
          {rows.map((row) => (
            <tr key={row._key}>
              <td>
                <input
                  type="text"
                  value={row.id ?? ""}
                  onChange={(e) => onChangeId(row, e.target.value)}
                />
              </td>
              <td>
                <MerchantHotspotStatusOption
                  merchantStatus={row.status}
                  onChange={(val) => onChangeStatus(row, val)}
                ></MerchantHotspotStatusOption>
              </td>
              <td>
                {!row.isCreatedByAutoUpdate && (
                  <span className="inline">
                      <span>
                        <input
                          type="number"
                          value={
                            UtilService.mapIsoOffsetDateTimeStringToHoursRemaining(
                              row.expirationTime
                            ) ?? ""
                          }
                          onChange={(e) => onChangeHours(row, e.target.value)}
                        />
                      </span>
                      <span>
                        &nbsp;
                        <FontAwesomeIcon
                          icon={faEraser}
                          className="icon-clickable"
                          onClick={() => onClearExpiration(row)}
                        ></FontAwesomeIcon>
                      </span>
                    </span>
                )}
              </td>
              <td>{row.isCreatedByAutoUpdate ? "YES" : "-"}</td>
              <td className="column-hide-overflow">
                {nameById.get(String(row.id)) || ""}
              </td>
              <td style={{ width: 44, textAlign: "right" }}>
                <FontAwesomeIcon
                  icon={faTrashCan}
                  className="icon-clickable"
                  onClick={() => onRemove(row)}
                ></FontAwesomeIcon>
              </td>
            </tr>
          ))}
          </tbody>
        </table>
      </main>
    </>
  );
}
